import { useEffect, useRef, useState } from "react";
import { Activity, ChevronDown, ChevronUp, ExternalLink, Eye, EyeOff, Loader2, Lock, LockOpen, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import api from "../api";
import { useLanguage } from "../i18n";
import { registerUnsaved, unregisterUnsaved } from "../utils/unsaved";

const SECTIONS = [
  { id: "stripe", title: "Stripe", descKey: "admin.intStripe", fields: ["stripe_secret_key", "stripe_webhook_secret"],
    consoleUrl: "https://dashboard.stripe.com/apikeys",
    urls: [{ labelKey: "admin.urlWebhook", path: "/api/stripe/webhook" }] },
  { id: "airalo", title: "Airalo Partner API", descKey: "admin.intAiralo", fields: ["airalo_base_url", "airalo_client_id", "airalo_client_secret"],
    consoleUrl: "https://partners.airalo.com/",
    urls: [{ labelKey: "admin.urlWebhook", path: "/api/airalo/webhook" }] },
  { id: "twilio", title: "Twilio Verify + SMS", descKey: "admin.intTwilio", fields: ["twilio_account_sid", "twilio_auth_token", "twilio_api_key", "twilio_api_secret", "twilio_verify_service", "twilio_messaging_service", "twilio_from_number"],
    consoleUrl: "https://console.twilio.com/" },
  { id: "resend", title: "Resend", descKey: "admin.intResend", fields: ["resend_api_key", "email_from_address"],
    consoleUrl: "https://resend.com/api-keys" },
  { id: "turnstile", title: "Cloudflare Turnstile", descKey: "admin.intTurnstile", fields: ["turnstile_site_key", "turnstile_secret_key"],
    consoleUrl: "https://dash.cloudflare.com/?to=/:account/turnstile" },
  { id: "fatture", title: "Fatture in Cloud", descKey: "admin.intFic", fields: ["fic_client_id", "fic_client_secret", "fic_redirect_uri"],
    consoleUrl: "https://developers.fattureincloud.it/",
    urls: [{ labelKey: "admin.urlRedirect", path: "/api/admin/fatture/callback" }] },
  { id: "firebase", title: "Firebase", descKey: "admin.intFirebase", fields: ["firebase_service_account"],
    consoleUrl: "https://console.firebase.google.com/project/_/settings/serviceaccounts/adminsdk" },
];

// Placeholder suggeriti per campi-URL (quando il campo è vuoto vale il default mostrato)
const FIELD_HINTS = { fic_redirect_uri: `${typeof window !== "undefined" ? window.location.origin : ""}/api/admin/fatture/callback` };

// Sistemi interni senza chiavi modificabili: solo stato (verde/rosso) con test live
const SYSTEMS = [
  { id: "mongodb", title: "MongoDB", descKey: "admin.sysMongo" },
  { id: "google_auth", title: "Login Google (Emergent Auth)", descKey: "admin.sysGoogle" },
  { id: "chatbot", title: "Chatbot AI", descKey: "admin.sysChat" },
];

const AUTOSAVE_MS = 2000;

export default function AdminIntegrations() {
  const { t } = useLanguage();
  const [data, setData] = useState(null);
  const [form, setForm] = useState({});
  const [dirty, setDirty] = useState({});
  const [visible, setVisible] = useState({});
  const [unlocked, setUnlocked] = useState({});  // lucchetto anti-salvataggio: campo modificabile solo se sbloccato
  const [testRes, setTestRes] = useState({});
  const [toggleBusy, setToggleBusy] = useState(false);
  const [saveState, setSaveState] = useState("idle"); // idle | pending | saving | saved | error
  const [history, setHistory] = useState(null);
  const [historyMore, setHistoryMore] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyBusy, setHistoryBusy] = useState(false);
  const [purgeArmed, setPurgeArmed] = useState(false);
  const [airaloBalance, setAiraloBalance] = useState(null);
  const [fic, setFic] = useState(null);

  const loadFic = () => api.get("/admin/fatture/status").then(({ data }) => setFic(data.data)).catch(() => setFic({ connected: false }));

  const ficConnect = async () => {
    try {
      const { data } = await api.get(`/admin/fatture/connect?front=${encodeURIComponent(window.location.origin)}`);
      window.location.assign(data.data.url);
    } catch (e) {
      toast.error(e.response?.data?.detail || t("misc.error"));
    }
  };

  const ficDisconnect = async () => {
    try {
      await api.post("/admin/fatture/disconnect");
      setFic({ connected: false });
      toast.success(t("fic.disconnected"));
      testSection(SECTIONS.find((s) => s.id === "fatture"));
    } catch {
      toast.error(t("misc.error"));
    }
  };
  const [verifyBusy, setVerifyBusy] = useState({});
  const stateRef = useRef({ form, dirty, data });
  stateRef.current = { form, dirty, data };
  const saveTimer = useRef(null);

  const load = () => api.get("/admin/integrations")
    .then(({ data }) => {
      setData(data.data);
      setForm((f) => ({ ...f, turnstile_enabled: data.data.turnstile_enabled, fic_business_only: data.data.fic_business_only, stripe_force_3ds: data.data.stripe_force_3ds }));
    })
    .catch(() => setData({ keys: {} }));

  // All'apertura della pagina lo stato di ogni servizio viene testato automaticamente
  useEffect(() => {
    load();
    api.get("/admin/airalo/balance").then(({ data }) => setAiraloBalance(data.data)).catch(() => setAiraloBalance(null));
    [...SECTIONS, ...SYSTEMS].forEach((s) => testSection(s));
    loadFic();
    // ritorno dal collegamento OAuth Fatture in Cloud
    const qs = new URLSearchParams(window.location.search);
    if (qs.get("fic") === "connected") toast.success(t("fic.connectedToast"));
    if (qs.get("fic") === "error") toast.error(t("misc.error"));
    if (qs.get("fic")) {
      qs.delete("fic");
      const q = qs.toString();
      window.history.replaceState({}, "", window.location.pathname + (q ? `?${q}` : ""));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Salvataggio automatico: 2 secondi dopo l'ultima modifica salva tutto da solo
  const saveAll = async () => {
    const { form: f, dirty: d } = stateRef.current;
    const payload = {};
    const affected = new Set();
    SECTIONS.forEach((sec) => sec.fields.forEach((k) => {
      if (d[k]) { payload[k] = f[k] ?? ""; affected.add(sec.id); }
    }));
    if (Object.keys(payload).length === 0) { setSaveState("idle"); return; }
    setSaveState("saving");
    try {
      const { data } = await api.post("/admin/integrations", payload);
      setData(data.data);
      setForm({ turnstile_enabled: data.data.turnstile_enabled, fic_business_only: data.data.fic_business_only, stripe_force_3ds: data.data.stripe_force_3ds });
      setDirty({});
      setVisible({});
      setUnlocked({});  // dopo il salvataggio i campi si ribloccano da soli
      setSaveState("saved");
      affected.forEach((id) => testSection(SECTIONS.find((s) => s.id === id)));
      if (history !== null) loadHistory(0);
      setTimeout(() => setSaveState((s) => (s === "saved" ? "idle" : s)), 3000);
    } catch (e) {
      setSaveState("error");
      toast.error(t("misc.error"));
      throw e;
    }
  };

  useEffect(() => {
    if (!Object.values(dirty).some(Boolean)) return undefined;
    setSaveState("pending");
    clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => { saveAll().catch(() => {}); }, AUTOSAVE_MS);
    return () => clearTimeout(saveTimer.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form, dirty]);

  // Registro modifiche non salvate: se esci prima dell'auto-salvataggio, il tab Admin
  // mostra il dialogo di conferma (salva/esci/annulla)
  useEffect(() => {
    const h = {
      dirty: () => Object.values(stateRef.current.dirty).some(Boolean),
      save: () => saveAll(),
      discard: () => {
        const d = stateRef.current.data;
        setForm({ turnstile_enabled: d?.turnstile_enabled, fic_business_only: d?.fic_business_only, stripe_force_3ds: d?.stripe_force_3ds });
        setDirty({});
        setVisible({});
        setUnlocked({});
        setSaveState("idle");
      },
    };
    registerUnsaved(h);
    return () => unregisterUnsaved(h);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setField = (k, v) => {
    setForm((f) => ({ ...f, [k]: v }));
    setDirty((d) => ({ ...d, [k]: true }));
    if (v === "") setVisible((vis) => ({ ...vis, [k]: false }));
  };

  const toggleShow = async (k) => {
    if (visible[k]) {
      setVisible((v) => ({ ...v, [k]: false }));
      setForm((f) => ({ ...f, [k]: "" }));
      setDirty((d) => ({ ...d, [k]: false }));
      return;
    }
    if ((form[k] || "") !== "") {
      setVisible((v) => ({ ...v, [k]: true }));
      return;
    }
    try {
      const { data } = await api.get(`/admin/integrations/reveal?key=${k}`);
      setForm((f) => ({ ...f, [k]: data.data.value || "" }));
      setDirty((d) => ({ ...d, [k]: true }));
      setVisible((v) => ({ ...v, [k]: true }));
    } catch {
      toast.error(t("misc.error"));
    }
  };

  // I flag si salvano SUBITO, senza bisogno del tasto Salva
  const toggleTurnstile = async (val) => {
    setToggleBusy(true);
    setForm((f) => ({ ...f, turnstile_enabled: val }));
    try {
      const { data } = await api.post("/admin/integrations", { turnstile_enabled: val });
      setData(data.data);
      toast.success(t("admin.saved"));
      testSection(SECTIONS.find((s) => s.id === "turnstile"));
      if (history !== null) loadHistory(0);
    } catch {
      setForm((f) => ({ ...f, turnstile_enabled: !val }));
      toast.error(t("misc.error"));
    } finally {
      setToggleBusy(false);
    }
  };

  // Flag "Forza 3DS" sui pagamenti con carta salvata: salvataggio immediato
  const toggleForce3ds = async (val) => {
    setToggleBusy(true);
    setForm((f) => ({ ...f, stripe_force_3ds: val }));
    try {
      const { data } = await api.post("/admin/integrations", { stripe_force_3ds: val });
      setData(data.data);
      toast.success(t("admin.saved"));
      testSection(SECTIONS.find((s) => s.id === "stripe"));
      if (history !== null) loadHistory(0);
    } catch {
      setForm((f) => ({ ...f, stripe_force_3ds: !val }));
      toast.error(t("misc.error"));
    } finally {
      setToggleBusy(false);
    }
  };

  // Flag "fatture solo Business": salvataggio immediato come gli altri flag
  const toggleFicBusinessOnly = async (val) => {
    setToggleBusy(true);
    setForm((f) => ({ ...f, fic_business_only: val }));
    try {
      const { data } = await api.post("/admin/integrations", { fic_business_only: val });
      setData(data.data);
      toast.success(t("admin.saved"));
    } catch {
      setForm((f) => ({ ...f, fic_business_only: !val }));
      toast.error(t("misc.error"));
    } finally {
      setToggleBusy(false);
    }
  };

  const testSection = async (sec) => {
    setTestRes((r) => ({ ...r, [sec.id]: "busy" }));
    try {
      const { data } = await api.post("/admin/integrations/test", { service: sec.id });
      setTestRes((r) => ({ ...r, [sec.id]: data }));
      return data;
    } catch (e) {
      const err = { ok: false, detail: e.response?.data?.detail || t("misc.error") };
      setTestRes((r) => ({ ...r, [sec.id]: err }));
      return err;
    }
  };

  // "Verifica e salva": salva subito le chiavi modificate e testa il servizio dal vivo.
  // Se il test passa i campi si ribloccano; se fallisce restano aperti per correggere.
  const verifySection = async (sec) => {
    setVerifyBusy((b) => ({ ...b, [sec.id]: true }));
    try {
      await saveAll();
    } catch {
      setVerifyBusy((b) => ({ ...b, [sec.id]: false }));
      return;
    }
    const res = await testSection(sec);
    if (res?.ok) {
      toast.success(t("admin.verifyOk"));
    } else {
      setUnlocked((u) => {
        const n = { ...u };
        sec.fields.forEach((k) => { n[k] = true; });
        return n;
      });
      toast.error(res?.detail || t("misc.error"));
    }
    setVerifyBusy((b) => ({ ...b, [sec.id]: false }));
  };

  const loadHistory = async (skip = 0) => {
    setHistoryBusy(true);
    try {
      const { data } = await api.get(`/admin/integrations/history?skip=${skip}&limit=25`);
      setHistory((old) => (skip === 0 || !old ? data.data || [] : [...old, ...(data.data || [])]));
      setHistoryMore(skip + (data.data || []).length < (data.total || 0));
    } catch { if (skip === 0) setHistory([]); } finally { setHistoryBusy(false); }
  };

  // Archiviazione voci >90 giorni: doppio clic di conferma (il tasto si "arma" per 4s)
  const purgeHistory = async () => {
    if (!purgeArmed) {
      setPurgeArmed(true);
      setTimeout(() => setPurgeArmed(false), 4000);
      return;
    }
    setPurgeArmed(false);
    setHistoryBusy(true);
    try {
      const { data } = await api.post("/admin/integrations/history/purge");
      toast.success(`${data.archived} ${t("admin.purged")}`);
      await loadHistory(0);
    } catch {
      toast.error(t("misc.error"));
    } finally {
      setHistoryBusy(false);
    }
  };

  if (!data) {
    return <div className="p-10 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-[#FF6B35]" /></div>;
  }

  return (
    <div data-testid="admin-integrations-panel" className="p-5 sm:p-7 max-w-3xl">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h3 className="font-display font-bold text-lg text-slate-900">{t("admin.integrations")}</h3>
          <p className="mt-1 text-sm text-slate-500">{t("admin.intDesc")}</p>
          {data.updated_at && (
            <p className="mt-2 font-mono-t text-[10px] uppercase tracking-widest text-slate-400">
              {new Date(data.updated_at).toLocaleString()} · {data.updated_by || "—"}
            </p>
          )}
        </div>
        <div data-testid="admin-int-autosave" className="shrink-0 pt-1 font-mono-t text-[10px] font-bold uppercase tracking-widest">
          {saveState === "pending" && <span className="text-amber-500">● {t("admin.autoPending")}</span>}
          {saveState === "saving" && <span className="text-slate-400 flex items-center gap-1.5"><Loader2 className="w-3 h-3 animate-spin" /> {t("admin.autoSaving")}</span>}
          {saveState === "saved" && <span className="text-emerald-600">✓ {t("admin.autoSaved")}</span>}
          {saveState === "error" && <span className="text-red-500">✗ {t("admin.autoError")}</span>}
          {saveState === "idle" && <span className="text-slate-300">{t("admin.autoNote")}</span>}
        </div>
      </div>

      {SECTIONS.map((sec) => {
        const res = testRes[sec.id];
        return (
          <section key={sec.id} data-testid={`admin-int-section-${sec.id}`} className="mt-6 rounded-3xl border border-slate-200/80 p-5 sm:p-6">
            <div className="flex items-start justify-between gap-4">
              <div>
                <h4 className="font-display font-bold text-slate-900 flex items-center gap-2">
                  {sec.title}
                  {res && res !== "busy" && (
                    <span data-testid={`admin-int-status-${sec.id}`}
                      className={`inline-block w-2.5 h-2.5 rounded-full ${res.ok ? "bg-emerald-500" : "bg-red-500"}`} />
                  )}
                  {res === "busy" && <span className="inline-block w-2.5 h-2.5 rounded-full bg-slate-300 animate-pulse" />}
                </h4>
                <p className="mt-0.5 text-xs text-slate-400">{t(sec.descKey)}</p>
                {sec.consoleUrl && sec.fields.some((f) => unlocked[f]) && (
                  <a data-testid={`admin-int-console-${sec.id}`} href={sec.consoleUrl} target="_blank" rel="noreferrer"
                    className="mt-1.5 inline-flex items-center gap-1.5 font-mono-t text-[10px] font-bold uppercase tracking-widest text-[#FF6B35] hover:text-[#E8551F] transition-colors">
                    <ExternalLink className="w-3 h-3" /> {t("admin.whereKey")}
                  </a>
                )}
                {sec.id === "airalo" && airaloBalance && (
                  <p data-testid="admin-airalo-balance" className="mt-2 text-sm font-semibold text-slate-800">
                    {t("admin.airaloBalance")}: <span className="text-[#0D9488]">€ {Number(airaloBalance.amount_eur ?? 0).toFixed(2)}</span>
                    <span className="text-slate-400"> (≈ {airaloBalance.amount} {airaloBalance.currency})</span>
                  </p>
                )}
              </div>
              <div className="shrink-0 flex items-center gap-2">
                {sec.fields.some((f) => unlocked[f]) && (
                  <button data-testid={`admin-int-verify-${sec.id}`} onClick={() => verifySection(sec)} disabled={verifyBusy[sec.id]}
                    className="rounded-full bg-[#FF6B35] text-white px-5 py-2 font-mono-t text-[10px] font-bold uppercase tracking-widest hover:bg-[#E8551F] transition-colors disabled:opacity-40 flex items-center gap-1.5">
                    {verifyBusy[sec.id] && <Loader2 className="w-3 h-3 animate-spin" />}
                    {t("admin.verifySave")}
                  </button>
                )}
                <button data-testid={`admin-int-test-${sec.id}`} onClick={() => testSection(sec)} disabled={res === "busy"}
                  className="shrink-0 rounded-full border border-slate-200 text-slate-600 px-5 py-2 font-mono-t text-[10px] font-bold uppercase tracking-widest hover:border-[#0D9488] hover:text-[#0D9488] transition-colors disabled:opacity-40 flex items-center gap-1.5">
                  {res === "busy" ? <Loader2 className="w-3 h-3 animate-spin" /> : <Activity className="w-3 h-3" />}
                  {t("admin.test")}
                </button>
              </div>
            </div>

            {res && res !== "busy" && (
              <p data-testid={`admin-int-test-result-${sec.id}`}
                className={`mt-3 rounded-2xl px-4 py-2.5 text-xs font-medium ${res.ok ? "bg-emerald-50 text-emerald-700" : "bg-red-50 text-red-600"}`}>
                {res.ok ? "✓" : "✗"} {res.detail}
              </p>
            )}

            {sec.id === "fatture" && (
              <div data-testid="admin-fic-oauth" className="mt-4 flex flex-wrap items-center justify-between gap-3 rounded-2xl bg-[#FAF5EE] px-4 py-3">
                <span data-testid="admin-fic-status" className={`text-sm font-medium ${fic?.connected ? "text-emerald-700" : "text-slate-500"}`}>
                  {fic?.connected
                    ? `${t("fic.connected")}: ${fic.company_name || fic.company_id}${fic.reauthorization_required ? ` — ${t("fic.reauth")}` : ""}`
                    : t("fic.notConnected")}
                </span>
                {fic?.connected ? (
                  <button data-testid="admin-fic-disconnect" onClick={ficDisconnect}
                    className="rounded-full border border-slate-200 px-5 py-2 font-mono-t text-[10px] font-bold uppercase tracking-widest text-slate-500 hover:border-red-400 hover:text-red-500 transition-colors">
                    {t("fic.disconnect")}
                  </button>
                ) : (
                  <button data-testid="admin-fic-connect" onClick={ficConnect}
                    className="rounded-full bg-[#FF6B35] px-5 py-2 font-mono-t text-[10px] font-bold uppercase tracking-widest text-white hover:bg-[#E8551F] transition-colors">
                    {t("fic.connect")}
                  </button>
                )}
              </div>
            )}

            {sec.id === "fatture" && (
              <label className={`mt-3 flex items-center gap-3 select-none ${toggleBusy ? "opacity-60 pointer-events-none" : "cursor-pointer"}`}>
                <input data-testid="admin-fic-business-only-toggle" type="checkbox" checked={form.fic_business_only !== false}
                  onChange={(e) => toggleFicBusinessOnly(e.target.checked)}
                  className="w-4 h-4 accent-[#FF6B35]" />
                <span className="text-sm font-medium text-slate-700">{t("fic.businessOnly")}</span>
                {toggleBusy && <Loader2 className="w-3.5 h-3.5 animate-spin text-[#FF6B35]" />}
              </label>
            )}

            {sec.id === "stripe" && (
              <div className="mt-3">
                <label className={`flex items-center gap-3 select-none ${toggleBusy ? "opacity-60 pointer-events-none" : "cursor-pointer"}`}>
                  <input data-testid="admin-int-force3ds-toggle" type="checkbox" checked={!!form.stripe_force_3ds}
                    onChange={(e) => toggleForce3ds(e.target.checked)}
                    className="w-4 h-4 accent-[#FF6B35]" />
                  <span className="text-sm font-medium text-slate-700">{t("admin.stripeForce3ds")}</span>
                  {toggleBusy && <Loader2 className="w-3.5 h-3.5 animate-spin text-[#FF6B35]" />}
                </label>
                <p className="mt-1.5 ml-7 text-xs text-slate-400 leading-relaxed">{t("admin.stripeForce3dsDesc")}</p>
              </div>
            )}

            {sec.id === "turnstile" && (
              <label className={`mt-4 flex items-center gap-3 select-none ${toggleBusy ? "opacity-60 pointer-events-none" : "cursor-pointer"}`}>
                <input data-testid="admin-int-turnstile-toggle" type="checkbox" checked={!!form.turnstile_enabled}
                  onChange={(e) => toggleTurnstile(e.target.checked)}
                  className="w-4 h-4 accent-[#FF6B35]" />
                <span className="text-sm font-medium text-slate-700">{t("admin.turnstileEnabled")}</span>
                {toggleBusy && <Loader2 className="w-3.5 h-3.5 animate-spin text-[#FF6B35]" />}
              </label>
            )}

            {sec.urls?.length > 0 && (
              <div className="mt-4 space-y-2">
                {sec.urls.map((u) => {
                  const full = `${window.location.origin}${u.path}`;
                  return (
                    <div key={u.path} data-testid={`admin-int-url-${sec.id}`}
                      className="flex items-center justify-between gap-3 rounded-2xl bg-[#FAF5EE] px-4 py-2.5">
                      <div className="min-w-0">
                        <div className="font-mono-t text-[9px] font-bold uppercase tracking-widest text-slate-400">{t(u.labelKey)}</div>
                        <div className="font-mono text-xs text-slate-700 truncate">{full}</div>
                      </div>
                      <button data-testid={`admin-int-url-copy-${sec.id}`}
                        onClick={() => { navigator.clipboard.writeText(full); toast.success(t("share.copied")); }}
                        className="shrink-0 rounded-full border border-slate-200 px-4 py-1.5 font-mono-t text-[9px] font-bold uppercase tracking-widest text-slate-500 hover:border-[#FF6B35] hover:text-[#FF6B35] transition-colors">
                        {t("admin.copy")}
                      </button>
                    </div>
                  );
                })}
              </div>
            )}

            <div className="mt-4 grid gap-4">
              {sec.fields.map((k) => {
                const meta = (data.keys || {})[k] || {};
                const [badgeCls, badgeLabel] = meta.source === "db"
                  ? ["bg-emerald-50 text-emerald-600", t("admin.keyDb")]
                  : meta.source === "env"
                    ? ["bg-slate-100 text-slate-500", t("admin.keyEnv")]
                    : ["bg-red-50 text-red-500", t("admin.keyNone")];
                return (
                  <div key={k}>
                    <div className="flex items-center justify-between mb-1.5">
                      <label className="font-mono-t text-[10px] font-bold uppercase tracking-widest text-slate-400">{k.toUpperCase()}</label>
                      <div className="flex items-center gap-2">
                        {dirty[k] && <span className="inline-block w-2 h-2 rounded-full bg-amber-400" title={t("admin.autoPending")} />}
                        <span data-testid={`admin-int-source-${k}`} className={`rounded-full px-2.5 py-0.5 font-mono-t text-[9px] font-bold uppercase tracking-widest ${badgeCls}`}>{badgeLabel}</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <input data-testid={`admin-int-input-${k}`} type={visible[k] ? "text" : "password"}
                        value={form[k] ?? ""} onChange={(e) => setField(k, e.target.value)}
                        disabled={!unlocked[k]}
                        placeholder={meta.masked || FIELD_HINTS[k] || t("admin.intPlaceholder")} autoComplete="off" spellCheck="false"
                        className={`w-full rounded-full border px-5 py-2.5 text-sm font-mono outline-none transition-colors ${
                          unlocked[k]
                            ? "border-slate-200 bg-transparent focus:border-[#FF6B35]"
                            : "border-slate-100 bg-slate-50 text-slate-400 cursor-not-allowed"}`} />
                      <button data-testid={`admin-int-lock-${k}`}
                        onClick={() => setUnlocked((u) => ({ ...u, [k]: !u[k] }))}
                        title={unlocked[k] ? t("admin.lockField") : t("admin.unlockField")}
                        className={`shrink-0 rounded-full border p-2.5 transition-colors ${
                          unlocked[k]
                            ? "border-[#FF6B35] text-[#FF6B35] bg-orange-50"
                            : "border-slate-200 text-slate-400 hover:border-[#FF6B35] hover:text-[#FF6B35]"}`}>
                        {unlocked[k] ? <LockOpen className="w-4 h-4" /> : <Lock className="w-4 h-4" />}
                      </button>
                      <button data-testid={`admin-int-show-${k}`} onClick={() => toggleShow(k)}
                        disabled={!unlocked[k] && !visible[k]}
                        title={visible[k] ? t("admin.hide") : t("admin.show")}
                        className="shrink-0 rounded-full border border-slate-200 p-2.5 text-slate-500 hover:border-[#FF6B35] hover:text-[#FF6B35] transition-colors disabled:opacity-30 disabled:pointer-events-none">
                        {visible[k] ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                      <button data-testid={`admin-int-reset-${k}`} onClick={() => setField(k, "")} title={t("admin.resetEnv")}
                        disabled={!unlocked[k]}
                        className="shrink-0 rounded-full border border-slate-200 p-2.5 text-slate-500 hover:border-red-400 hover:text-red-500 transition-colors disabled:opacity-30 disabled:pointer-events-none">
                        <RotateCcw className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </section>
        );
      })}

      <section data-testid="admin-int-section-systems" className="mt-6 rounded-3xl border border-slate-200/80 p-5 sm:p-6">
        <h4 className="font-display font-bold text-slate-900">{t("admin.intSystems")}</h4>
        <div className="mt-4 space-y-1.5">
          {SYSTEMS.map((s) => {
            const res = testRes[s.id];
            return (
              <div key={s.id} className="flex items-center justify-between gap-3 rounded-2xl px-4 py-3 hover:bg-[#FAF5EE] transition-colors">
                <div className="flex items-center gap-3 min-w-0">
                  {!res || res === "busy" ? (
                    <span className="inline-block w-2.5 h-2.5 rounded-full bg-slate-300 animate-pulse shrink-0" />
                  ) : (
                    <span data-testid={`admin-int-status-${s.id}`} className={`inline-block w-2.5 h-2.5 rounded-full shrink-0 ${res.ok ? "bg-emerald-500" : "bg-red-500"}`} />
                  )}
                  <div className="min-w-0">
                    <div className="text-sm font-semibold text-slate-800">{s.title}</div>
                    <div data-testid={`admin-int-test-result-${s.id}`} className="text-xs text-slate-400 truncate">
                      {res && res !== "busy" ? `${res.ok ? "✓" : "✗"} ${res.detail}` : t(s.descKey)}
                    </div>
                  </div>
                </div>
                <button data-testid={`admin-int-test-${s.id}`} onClick={() => testSection(s)} disabled={res === "busy"}
                  title={t("admin.test")}
                  className="shrink-0 rounded-full border border-slate-200 p-2.5 text-slate-500 hover:border-[#0D9488] hover:text-[#0D9488] transition-colors disabled:opacity-40">
                  {res === "busy" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Activity className="w-4 h-4" />}
                </button>
              </div>
            );
          })}
        </div>
      </section>

      <section className="mt-8 rounded-3xl border border-slate-200/80 p-5 sm:p-6">
        <button data-testid="admin-int-history-toggle"
          onClick={() => { const o = !historyOpen; setHistoryOpen(o); if (o && history === null) loadHistory(0); }}
          className="w-full flex items-center justify-between text-left">
          <div>
            <h4 className="font-display font-bold text-slate-900">{t("admin.history")}</h4>
            <p className="mt-0.5 text-xs text-slate-400">{t("admin.historyDesc")}</p>
          </div>
          {historyOpen ? <ChevronUp className="w-4 h-4 text-slate-400 shrink-0" /> : <ChevronDown className="w-4 h-4 text-slate-400 shrink-0" />}
        </button>
        {historyOpen && (
          <div data-testid="admin-int-history" className="mt-4 space-y-1.5">
            <div className="flex justify-end">
              <button data-testid="admin-int-history-purge" onClick={purgeHistory} disabled={historyBusy}
                className={`rounded-full border px-5 py-2 font-mono-t text-[10px] font-bold uppercase tracking-widest transition-colors disabled:opacity-50 ${purgeArmed ? "border-red-400 text-red-500 bg-red-50" : "border-slate-200 text-slate-500 hover:border-[#FF6B35] hover:text-[#FF6B35]"}`}>
                {purgeArmed ? t("admin.purgeConfirm") : t("admin.purgeOld")}
              </button>
            </div>
            {history === null ? (
              <div className="py-6 text-center"><Loader2 className="w-5 h-5 animate-spin mx-auto text-[#FF6B35]" /></div>
            ) : history.length === 0 ? (
              <p className="py-6 text-left text-sm text-slate-400">{t("admin.noLogs")}</p>
            ) : (
              history.map((h, i) => (
                <div key={h.log_id || i} data-testid={`admin-int-history-${i}`}
                  className="flex flex-wrap items-baseline gap-x-3 gap-y-1 rounded-xl px-4 py-2.5 hover:bg-[#FAF5EE] transition-colors">
                  <span className="font-mono-t text-[10px] text-slate-400 shrink-0">{new Date(h.ts).toLocaleString()}</span>
                  <span className="text-sm text-slate-700 flex-1 min-w-[200px]">{String(h.message || "").replace(/^Integrazioni aggiornate da /, "")}</span>
                </div>
              ))
            )}
            {historyMore && (
              <div className="pt-2 text-center">
                <button data-testid="admin-int-history-more" onClick={() => loadHistory((history || []).length)} disabled={historyBusy}
                  className="rounded-full border border-slate-200 px-6 py-2.5 font-mono-t text-[10px] font-bold uppercase tracking-widest text-slate-600 hover:border-[#FF6B35] hover:text-[#FF6B35] transition-colors disabled:opacity-50">
                  {historyBusy ? "…" : t("admin.showMore")}
                </button>
              </div>
            )}
          </div>
        )}
      </section>
    </div>
  );
}
